/** Findings have no acceptance stage. Suggestions have a separate lifecycle. */
export const FINDING_STATUSES = ["open", "resolved", "closed"] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

/** Read compatibility during rollout; legacy values are never valid new decisions. */
export function normalizeFindingStatus(value: unknown): FindingStatus | null {
  if (value === "accepted") return "open";
  if (value === "dismissed") return "closed";
  return value === "open" || value === "resolved" || value === "closed" ? value : null;
}

/**
 * Whether a decision request repeats the decision already on record — the same
 * status, version and note. A resolve whose response was lost is retried as
 * exactly this, and must succeed with the record as it stands rather than be
 * told to reopen what it just decided. Anything different is a new decision,
 * and a decided finding takes none until it is reopened.
 */
export function repeatsFindingDecision(
  existing: {
    readonly status: unknown;
    readonly resolvedVersion?: number | null | undefined;
    readonly resolutionNote?: string | null | undefined;
  },
  request: {
    readonly status: string;
    readonly resolvedVersion?: number | null | undefined;
    readonly note?: string | null | undefined;
  },
): boolean {
  const status = normalizeFindingStatus(existing.status);
  if (status === null || status === "open" || status !== request.status) return false;
  const version = request.status === "resolved" ? (request.resolvedVersion ?? null) : null;
  return (
    (existing.resolvedVersion ?? null) === version &&
    (existing.resolutionNote ?? null) === (request.note ?? null)
  );
}
